/**
 * Provider Resource Aggregator
 * Aggregates individual resource arrays into a unified resources array
 */

import {
    Invoice,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderClaimTransmission,
    ProviderCoding,
    ProviderCollectionPayment,
    ProviderComment,
    ProviderDocument,
    ProviderInstitutionalClaim,
    ProviderLetter,
    ProviderMail,
    ProviderMessage,
    ProviderNynf3Claim,
    ProviderPhone,
    ProviderProfessionalClaim,
    ProviderReceiveFax,
    ProviderSendEmail,
    ProviderSendFax,
    ProviderStatus,
    SupportHL7Message,
    Transaction,
} from '@enter/core';
import _ from 'lodash';
import { ProviderClaimResource } from './resource-types';

export interface IAggregateResourcesParams {
    // Claim data (for step events)
    claim?: ProviderClaim;
    claimPind?: ProviderProfessionalClaim | ProviderInstitutionalClaim | ProviderNynf3Claim;

    // Resource arrays
    codings?: ProviderCoding[];
    collections?: ProviderCollectionPayment[];
    comments?: ProviderComment[];
    documents?: ProviderDocument[];
    eligibilities?: ProviderClaimEligibility[];
    hl7Messages?: SupportHL7Message[];
    invoices?: Invoice[];
    letters?: ProviderLetter[];
    mails?: ProviderMail[];
    phones?: ProviderPhone[];
    messages?: ProviderMessage[];
    payments?: ProviderClaimPayment[];
    receiveFaxes?: ProviderReceiveFax[];
    sendFaxes?: ProviderSendFax[];
    sendEmails?: ProviderSendEmail[];
    statuses?: ProviderStatus[];
    transmissions?: ProviderClaimTransmission[];
    transactions?: Transaction[];
}

/**
 * Aggregates all Provider resource arrays into a single unified resources array
 * sorted by creation date (descending - most recent first)
 */
export function aggregateClaimResources(params: IAggregateResourcesParams): ProviderClaimResource[] {
    const resources: ProviderClaimResource[] = [];

    // Add step events (Provider-specific synthetic resources)
    const stepEvents = createStepResources(params);
    resources.push(...stepEvents);

    // Map each resource type
    params.codings?.forEach(coding => resources.push({ ...coding, resourceType: 'CODING' }));

    params.collections?.forEach(collection => resources.push({ ...collection, resourceType: 'COLLECTION_PAYMENT' }));

    params.comments?.forEach(comment => resources.push({ ...comment, resourceType: 'COMMENT' }));

    params.documents?.forEach(doc => resources.push({ ...doc, resourceType: 'DOCUMENT' }));

    params.eligibilities?.forEach(eligibility => resources.push({ ...eligibility, resourceType: 'ELIGIBILITY' }));

    params.hl7Messages?.forEach(hl7 => resources.push({ ...hl7, resourceType: 'HL7' }));

    params.invoices?.forEach(invoice => resources.push({ ...invoice, resourceType: 'INVOICE' }));

    params.letters?.forEach(letter => resources.push({ ...letter, resourceType: 'LETTER' }));

    params.mails?.forEach(mail => resources.push({ ...mail, resourceType: 'MAIL' }));

    params.phones?.forEach(phone => resources.push({ ...phone, resourceType: 'PHONE' }));

    params.messages?.forEach(message => resources.push({ ...message, resourceType: 'MESSAGE' }));

    params.payments?.forEach(payment => resources.push({ ...payment, resourceType: 'PAYMENT' }));

    params.receiveFaxes?.forEach(fax => resources.push({ ...fax, resourceType: 'FAX_RECEIVE' }));

    params.sendFaxes?.forEach(fax => resources.push({ ...fax, resourceType: 'FAX_SEND' }));

    params.sendEmails?.forEach(email => resources.push({ ...email, resourceType: 'EMAIL_SEND' }));

    params.statuses?.forEach(status => resources.push({ ...status, resourceType: 'STATUS' }));

    params.transmissions?.forEach(transmission => resources.push({ ...transmission, resourceType: 'TRANSMISSION' }));

    params.transactions?.forEach(transaction => resources.push({ ...transaction, resourceType: 'TRANSACTION' }));

    // Sort by created date (messages use updated, others use created)
    // Descending order (most recent first) - same as Support
    return _.orderBy(
        resources,
        [
            resource => {
                if (resource.resourceType === 'MESSAGE') {
                    return (resource as any).updated || (resource as any).created;
                }
                return (resource as any).created;
            },
        ],
        ['desc'],
    );
}

/**
 * Creates synthetic step event resources for Provider
 * These represent lifecycle events: Claim Created, Received
 */
function createStepResources(params: IAggregateResourcesParams): ProviderClaimResource[] {
    const steps: ProviderClaimResource[] = [];
    const { claim, claimPind, payments } = params;

    // Claim Created
    if (claim && claimPind) {
        steps.push({
            resourceType: 'STEP',
            stepType: 'CREATED',
            id: 'claim-created',
            created: claimPind.created,
            event: 'Claim Created',
            description: 'Claim created',
            type: 'Enter Admin',
        });
    }

    // Claim Received
    const payment = _.first(_.sortBy(payments, 'created'));
    if (payment) {
        steps.push({
            resourceType: 'STEP',
            stepType: 'RECEIVED',
            id: 'claim-received',
            created: payment.created,
            event: 'Claim Received',
            description: 'Claim received',
            type: 'Enter Admin',
        });
    }

    return steps;
}
